import { Component } from 'react';

import { GoogleTagManager } from '@iwoca/frontend-tracking-library';
import classNames from 'classnames';
import { FormattedMessage, FormattedDate } from 'react-intl';
import { reduxForm } from 'redux-form';

import messages from './SelectAmountWithSlider.messages';
import styles from './SelectAmountWithSlider.module.css';
import {
    normalizeSliderValue,
    denormlizeSliderValue,
    normalizeInputValue,
    denormalizeInputValue,
    denormlizeSliderValueAmount,
    serialiseAndRoundInputValue
} from './SliderHelper';
import { FormField } from '../../../forms/formField/';
import { getCurrenySymbolFromCode } from '../../../helpers/currency';
import { getCountry } from '../../../iwIntlProvider/CountryProvider';
import { TooltipQuestionMarkSVG } from '../../../svgComponents/tooltipQuestionMarkSVG/index';
import { MoneyNoDecimals } from '../moneyNoDecimals/';
import { Tooltip } from '../tooltip/index';

const formConfiguration = {
    form: 'selectAmountWithSlider'
};

class SelectAmountWithSlider extends Component {
    trackAndRequestSelectedAmount = (...args) => {
        const { name, selectedAmount } = this.props;

        GoogleTagManager.trackSlider({
            sliderName: name,
            sliderValue: selectedAmount
        });

        this.props.requestSelectedAmount(...args);
    };

    handleAmountInputSubmit = (event) => {
        event.preventDefault();
        const {
            updateSelectedAmount,
            maxValue,
            minValue,
            amountInputForm,
            change
        } = this.props;

        let inputValue = amountInputForm?.values?.amount;

        let serialisedInputValue = serialiseAndRoundInputValue(
            inputValue,
            maxValue,
            minValue
        );

        if (serialisedInputValue <= minValue) {
            change('amount', minValue);
        }

        updateSelectedAmount(serialisedInputValue, serialisedInputValue);
        this.trackAndRequestSelectedAmount();
    };

    updateAndParse = (value) => {
        const { updateSelectedAmount } = this.props;
        const formattedVal = denormalizeInputValue(value);

        updateSelectedAmount(formattedVal, formattedVal);
    };

    handleFormatInput = (value) => {
        const { maxValue, minValue, locale } = this.props;

        if (!value) {
            return '';
        }
        return normalizeInputValue(value, maxValue, minValue, locale);
    };

    handleParseInput = (value) => {
        return denormalizeInputValue(value);
    };

    handleSliderChange = () => {
        const { updateSelectedAmount, changeForm, maxValue, minValue } =
            this.props;
        let sliderValue = this.slider.value;

        let sliderAmountDenormalised = denormlizeSliderValue(
            sliderValue,
            maxValue,
            minValue
        );
        let inputValue = denormlizeSliderValueAmount(
            sliderAmountDenormalised,
            maxValue,
            minValue
        );

        updateSelectedAmount(inputValue, sliderAmountDenormalised);
        changeForm(inputValue);
    };

    handleSliderValue = () => {
        const { selectedSliderAmount, maxValue, minValue } = this.props;
        return normalizeSliderValue(selectedSliderAmount, maxValue, minValue);
    };

    handleSliderOnAfterChange = () => {
        this.trackAndRequestSelectedAmount();
    };

    renderTooltip = () => {
        const { maxValue, approvalStatusTimeOut } = this.props;

        return (
            <Tooltip
                message={
                    <FormattedMessage
                        {...messages.tooltip}
                        values={{
                            amount: <MoneyNoDecimals value={maxValue} />,
                            date: (
                                <FormattedDate
                                    value={new Date(approvalStatusTimeOut)}
                                    day="2-digit"
                                    month="long"
                                    year="numeric"
                                />
                            )
                        }}
                    />
                }
            >
                <TooltipQuestionMarkSVG
                    className={classNames(styles.icon, styles.questionIcon)}
                />
            </Tooltip>
        );
    };

    render() {
        const { children, minValue, maxValue, currency, isHPB } = this.props;

        const currencySymbol = getCurrenySymbolFromCode(currency);
        const country = getCountry();
        const currencySymbolStyles = classNames(
            styles.currencySymbol,
            styles[`currencySymbol_${country}`],
            { [styles.currencySymbolHPB]: isHPB }
        );

        return (
            <div
                className={classNames(styles.SelectAmountWithSlider, {
                    [styles.SelectAmountWithSliderHPB]: isHPB
                })}
                data-testid="choose-amount-slider-title"
            >
                <div
                    className={classNames(styles.sliderTitle, {
                        [styles.sliderTitleHPB]: isHPB
                    })}
                >
                    <h3
                        className={classNames(styles.title, {
                            [styles.titleHPB]: isHPB
                        })}
                    >
                        {children}
                    </h3>
                    <div
                        className={classNames(styles.formWrapper, {
                            [styles.formWrapperHPB]: isHPB
                        })}
                    >
                        <form
                            className={classNames(styles.form, {
                                [styles.formHPB]: isHPB
                            })}
                            onSubmit={this.handleAmountInputSubmit}
                        >
                            <span
                                data-testid="choose-amount-input-currency"
                                className={currencySymbolStyles}
                            >
                                {currencySymbol}
                            </span>
                            <FormField
                                name="amount"
                                data-testid="choose-amount-input"
                                onChangeHandler={this.updateAndParse}
                                className={classNames(styles.amountInput, {
                                    [styles.amountInputHPB]: isHPB
                                })}
                                format={this.handleFormatInput}
                                parse={this.handleParseInput}
                                onBlur={this.handleAmountInputSubmit}
                            />
                        </form>
                    </div>
                    {isHPB && this.renderTooltip()}
                </div>
                <div className={styles.sliderContainer}>
                    <input
                        type="range"
                        ref={(slider) => {
                            this.slider = slider;
                        }}
                        className={styles.slider}
                        style={{
                            '--proportion': `${(this.handleSliderValue() / 1000) * 100}%`
                        }}
                        value={this.handleSliderValue()}
                        onChange={this.handleSliderChange}
                        onKeyUp={this.handleSliderOnAfterChange}
                        onMouseUp={this.handleSliderOnAfterChange}
                        onBlur={this.handleSliderOnAfterChange}
                        onTouchEnd={this.handleSliderOnAfterChange}
                        min={0}
                        max={1000}
                    />
                </div>
                <div className={styles.sliderMinMaxValues}>
                    <div
                        data-testid="minimum-drawdown-amount"
                        className={styles.min}
                    >
                        <MoneyNoDecimals value={minValue} />
                    </div>
                    <div
                        data-testid="maximum-drawdown-amount"
                        className={styles.max}
                    >
                        <MoneyNoDecimals value={maxValue} />
                    </div>
                </div>
            </div>
        );
    }
}

// eslint-disable-next-line import/no-default-export
export default reduxForm(formConfiguration)(SelectAmountWithSlider);
